import { Link } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";
import { articles } from "@/lib/articles";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";

// Proven reader-traffic articles shown first, then any other article with a cover, newest first.
const PROVEN = ["fair-value-gap-xauusd-guide", "gold-correction-5600-peak-macro-analysis"];

function pickArticles() {
  const withImage = articles.filter((a) => a.heroImage && a.visibility !== "academy");
  const newest = [...withImage].sort((a, b) => b.publishedAt.localeCompare(a.publishedAt));
  const proven = PROVEN.map((s) => withImage.find((a) => a.slug === s)).filter(Boolean) as typeof withImage;
  const rest = newest.filter((a) => !PROVEN.includes(a.slug));
  return [...rest.slice(0, 3), ...proven, ...rest.slice(3)];
}

export function LandingArticleSlider() {
  const items = pickArticles();
  if (items.length === 0) return null;
  return (
    <section className="border-b border-border/60 px-5 py-14 sm:px-8 lg:px-14 lg:py-20">
      <div className="mx-auto max-w-5xl">
        <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
          <div className="max-w-2xl">
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-gold">From the research desk</div>
            <h2 className="mt-3 font-display text-3xl font-semibold">Latest and most read studies.</h2>
          </div>
          <Link to="/education" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-gold">
            All articles <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        <Carousel opts={{ align: "start", loop: false }} className="relative">
          <CarouselContent className="-ml-4">
            {items.map((a) => (
              <CarouselItem key={a.slug} className="basis-[85%] pl-4 sm:basis-1/2 lg:basis-1/3">
                <Link
                  to="/education/$slug"
                  params={{ slug: a.slug }}
                  className="group flex h-full flex-col overflow-hidden rounded-md border border-border/60 bg-card/55 transition-colors hover:border-gold/40"
                >
                  <div className="aspect-video overflow-hidden bg-muted">
                    <img
                      src={a.heroImage}
                      alt={a.title}
                      loading="lazy"
                      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
                    />
                  </div>
                  <div className="flex flex-1 flex-col p-4">
                    <div className="font-mono text-[9px] uppercase tracking-[0.18em] text-gold">{a.category}</div>
                    <h3 className="mt-2 text-sm font-semibold leading-snug">{a.title}</h3>
                    <div className="mt-auto pt-3 font-mono text-[10px] uppercase tracking-[0.14em] text-muted-foreground">{a.readTime} read</div>
                  </div>
                </Link>
              </CarouselItem>
            ))}
          </CarouselContent>
          <CarouselPrevious className="-left-4 hidden md:flex" />
          <CarouselNext className="-right-4 hidden md:flex" />
        </Carousel>
      </div>
    </section>
  );
}
